/**
 * <s-gallery-counter> — swipe cue for the mobile product gallery.
 *
 * Shows "current / total" and prev/next buttons under the horizontally
 * scrolling gallery so shoppers can tell there are more photos to swipe.
 * Focus never moves; the counter updates as slides scroll into view.
 *
 * Required markup (inside the same <s-product-image-zoom> as the gallery):
 *   [data-product-gallery]  — the scroll-snap gallery; each child is a slide.
 *   [data-gallery-current]  — text node updated with the visible slide number.
 *   [data-gallery-prev], [data-gallery-next] — buttons that scroll one slide.
 */
class SGalleryCounter extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    const root = this.closest('s-product-image-zoom') ?? document;
    this.gallery = root.querySelector('[data-product-gallery]');
    if (!this.gallery) return;

    this.slides = Array.from(this.gallery.children);
    this.current = this.querySelector('[data-gallery-current]');
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.prev?.addEventListener('click', () => this.#go(this.#index - 1), opts);
    this.next?.addEventListener('click', () => this.#go(this.#index + 1), opts);
    // The variant picker moves the selected colour's photo to the front; re-read slide order.
    this.gallery.addEventListener('gallery:reorder', () => {
      this.slides = Array.from(this.gallery.children);
      this.#index = 0;
      this.#render();
    }, opts);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#index = this.slides.indexOf(entry.target);
            this.#render();
          }
        }
      },
      { root: this.gallery, threshold: 0.6 }
    );
    for (const slide of this.slides) this.#observer.observe(slide);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #go(index) {
    const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.gallery.scrollTo({
      left: target.offsetLeft - (this.gallery.clientWidth - target.clientWidth) / 2,
      behavior: reduce ? 'auto' : 'smooth',
    });
  }

  #render() {
    if (this.current) this.current.textContent = String(this.#index + 1);
    if (this.prev) this.prev.disabled = this.#index === 0;
    if (this.next) this.next.disabled = this.#index >= this.slides.length - 1;
  }
}

if (!customElements.get('s-gallery-counter')) {
  customElements.define('s-gallery-counter', SGalleryCounter);
}
